import { Button } from '@src/components/button/Button.component'
import { useTranslation } from '@src/hooks/useTranslation'
import { mixpanelService } from '@src/srv/mixpanel.service'
import { DatoCmsFileField } from 'dato-cms-assets'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { NcImage } from '../nc-image/NcImage.component'
import styles from './PopupSimple.module.scss'

export interface PopupSimpleProps {
  name: string
  isOpen: boolean
  _modelApiKey: 'simple_popup'
  title: string
  text: string
  buttonText: string
  buttonUrl: string
  image: DatoCmsFileField
  onDismiss: () => void
}

export function PopupSimple({
  name,
  isOpen,
  title,
  text,
  buttonText,
  buttonUrl,
  onDismiss,
  image,
}: PopupSimpleProps): ReactNode {
  const button = useRef<any>()
  const translate = useTranslation()
  const hasOpened = useRef(false)

  function handleOpen(): void {
    // Focusing an element inside the modal avoids the problem of the modal
    // appearing visually but going unnoticed by screen readers, whose focus
    // remains on the main page content behind, often with no clear path forward
    // for the user to rectify the situation.
    button.current.focus()

    hasOpened.current = true
    mixpanelService.track('PopupSimpleImpression', {
      name,
    })
  }

  function handleClose(): void {
    mixpanelService.track('PopupSimpleClose', {
      name,
    })
  }

  useEffect(() => {
    if (isOpen) {
      handleOpen()
    } else if (hasOpened.current) {
      handleClose()
    }
  }, [isOpen])

  const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
    'aria-label': translate('popup-close-button'),
    className: styles.PopupSimple__closeButton,
    onClick: onDismiss,
  }

  return (
    <div className={styles.PopupSimple}>
      <button {...buttonProps} ref={button}>
        <svg height={28} width={28} xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 42 42">
          <circle cx={18} cy={17.5} r={20} className={styles.PopupSimple__closeIconBackground} />
          <polygon
            className={styles.PopupSimple__closeIconForeground}
            points="27.661 9.366 26.325 8 18 16.63 9.675 8 8.339 9.366 16.661 18 8.339 26.634 9.675 28 18 19.37 26.325 28 27.661 26.634 19.339 18 27.661 9.366"
          />
        </svg>
      </button>
      <div className={styles.PopupSimple__image}>
        <NcImage {...image} objectFit="contain" />
      </div>
      <div className={styles.PopupSimple__content}>
        <div
          id="popup-title"
          className={styles.PopupSimple__title}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <div
          id="popup-description"
          className={styles.PopupSimple__text}
          dangerouslySetInnerHTML={{ __html: text }}
        />
        <div className={styles.PopupSimple__button}>
          <Button theme="brandPurple" href={buttonUrl}>
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  )
}
